import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
}

interface AuthActions {
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

export const useAuth = (): AuthState & AuthActions => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user has admin role
  const checkAdminRole = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'admin'
      });
      
      if (error) {
        console.error('Error checking admin role:', error);
        return false;
      }
      
      return data || false;
    } catch (err) {
      console.error('Error checking admin role:', err);
      return false;
    }
  };

  // Link orphaned client records when a user logs in
  const linkOrphanedClientRecord = async (userId: string, userEmail: string) => {
    try {
      const { data: orphanedClients } = await supabase
        .from('clients')
        .select('id')
        .ilike('email', userEmail.toLowerCase().trim())
        .is('user_id', null)
        .order('created_at', { ascending: true })
        .limit(1);

      if (orphanedClients?.[0]) {
        await supabase
          .from('clients')
          .update({ user_id: userId })
          .eq('id', orphanedClients[0].id);
      }
    } catch (err) {
      console.error('Error linking orphaned client record:', err);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST - must be synchronous to avoid deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // Defer admin check to avoid blocking the auth state change
        if (currentSession?.user) {
          setLoading(true);
          setTimeout(() => {
            const userId = currentSession.user.id;
            const userEmail = currentSession.user.email;

            // Check admin role
            checkAdminRole(userId)
              .then(setIsAdmin)
              .catch((err) => {
                console.error('Error checking admin role:', err);
                setIsAdmin(false);
              })
              .finally(() => setLoading(false));

            // On sign-in, link any orphaned client records
            if (event === 'SIGNED_IN' && userEmail) {
              linkOrphanedClientRecord(userId, userEmail);
            }
          }, 0);
        } else {
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      // Defer admin check to avoid blocking
      if (currentSession?.user) {
        setTimeout(() => {
          checkAdminRole(currentSession.user.id)
            .then(setIsAdmin)
            .catch((err) => {
              console.error('Error checking admin role:', err);
              setIsAdmin(false);
            })
            .finally(() => setLoading(false));
        }, 0);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      return { error };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
          }
        }
      });
      
      return { error };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
  };

  return {
    user,
    session,
    loading,
    isAdmin,
    signIn,
    signUp,
    signOut,
  };
};
